import { GridCellParams } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import { kebabCase, startCase } from 'lodash';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedDatePickerOperator,
  advancedEnumOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getFormattedDate } from '../../../utils/helpers';
import { ChangeHistoryRecord } from '../../../store/ChangeHistory';
import { Text } from '../../Atoms/Text';

// Regex to detect ISO date strings with optional timezone
const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;

export const DefaultHistoryGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'changedAt',
    headerName: 'When',
    sortable: true,
    filterable: true,
    minWidth: 160,
    flex: 0.8,
    description: 'Drag to move column',
    valueFormatter: (value) => getFormattedDate(value, 'lll'),
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'recordTitle',
    headerName: 'Record',
    sortable: true,
    filterable: true,
    minWidth: 140,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    renderCell: (params: GridCellParams<ChangeHistoryRecord>) => {
      const { recordType, recordId, recordTitle } = params.row;
      if (!recordId) return <span>{recordTitle || startCase(recordType)}</span>;
      const label = recordTitle || startCase(recordType);
      return (
        <Link
          className="flex items-center"
          to={`/${kebabCase(recordType)}/${recordId}`}
          target="_blank"
          rel="noopener noreferrer">
          <Text
            size="sm14"
            color="indigo">
            {label}
          </Text>
        </Link>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueChangeCategory',
    headerName: 'Category',
    sortable: true,
    filterable: true,
    minWidth: 140,
    flex: 0.8,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: ['provisional_asset', 'asset', 'ticket'],
    getOptionLabel: (value) => startCase(String(value)),
    getOptionValue: (value) => value,
    field: 'recordType',
    headerName: 'Record Type',
    sortable: true,
    filterable: true,
    minWidth: 140,
    flex: 0.8,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'field',
    headerName: 'Field',
    sortable: true,
    filterable: true,
    minWidth: 230,
    flex: 0.8,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'fromValue',
    headerName: 'From',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 0.8,
    description: 'Drag to move column',
    valueFormatter: (value) => {
      if (value && dateRegex.test(value)) {
        return getFormattedDate(value);
      }
      return value;
    },
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'toValue',
    headerName: 'To',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 0.8,
    description: 'Drag to move column',
    valueFormatter: (value) => {
      if (value && dateRegex.test(value)) {
        return getFormattedDate(value);
      }
      return value;
    },
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'changedByName',
    headerName: 'By',
    sortable: true,
    filterable: true,
    minWidth: 230,
    flex: 0.8,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'changeSource',
    headerName: 'Source',
    sortable: true,
    filterable: true,
    minWidth: 130,
    flex: 0.6,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: ['Insert', 'Update', 'Delete'],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'operation',
    headerName: 'Operation',
    sortable: true,
    filterable: true,
    minWidth: 110,
    flex: 0.6,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    hideByDefault: true
  }
];
